export const EXERCISE_EXECUTION_SPEEDS = ['isometry', 'cadence'] as const;

export type ExerciseExecutionSpeed = (typeof EXERCISE_EXECUTION_SPEEDS)[number];

export const EXERCISE_EXECUTION_SPEED_OPTIONS: Array<{ value: ExerciseExecutionSpeed; label: string }> = [
  { value: 'isometry', label: 'Isometria' },
  { value: 'cadence', label: 'Cadência' },
];

export const EXERCISE_EXECUTION_SPEED_LABELS: Record<ExerciseExecutionSpeed, string> = {
  isometry: 'Isometria',
  cadence: 'Cadência',
};

export const DEFAULT_REST_SECONDS = 60;
export const DEFAULT_ISOMETRIC_SECONDS = 1;
export const DEFAULT_CONCENTRIC_SECONDS = 1;
export const DEFAULT_PAUSE_SECONDS = 0;
export const DEFAULT_ECCENTRIC_SECONDS = 1;

const LEGACY_EXECUTION_SPEED_MAP: Record<string, ExerciseExecutionSpeed> = {
  isometry: 'isometry',
  isometria: 'isometry',
  cadence: 'cadence',
  cadencia: 'cadence',
  cadência: 'cadence',
};

export const isExerciseExecutionSpeed = (value?: string | null): value is ExerciseExecutionSpeed => (
  value === 'isometry' || value === 'cadence'
);

export const normalizeExecutionSpeed = (value?: string | null): ExerciseExecutionSpeed | null => {
  const trimmedValue = value?.trim() ?? '';

  if (!trimmedValue) {
    return null;
  }

  const mappedValue = LEGACY_EXECUTION_SPEED_MAP[trimmedValue.toLowerCase()];

  if (mappedValue) {
    return mappedValue;
  }

  if (trimmedValue.toLowerCase().includes('isometr')) {
    return 'isometry';
  }

  return 'cadence';
};

export const isIsometryExecutionSpeed = (value?: string | null) => (
  normalizeExecutionSpeed(value) === 'isometry'
);

export const isCadenceExecutionSpeed = (value?: string | null) => (
  normalizeExecutionSpeed(value) === 'cadence'
);

export const parseCadencePhaseSeconds = (value: unknown): number | null => {
  const parsedValue = typeof value === 'number'
    ? value
    : typeof value === 'string' && value.trim() !== ''
      ? Number.parseInt(value, 10)
      : Number.NaN;

  if (!Number.isFinite(parsedValue) || parsedValue < 0) {
    return null;
  }

  return Math.trunc(parsedValue);
};

export const resolveCadencePhaseSeconds = (value: unknown, fallback: number) => (
  parseCadencePhaseSeconds(value) ?? fallback
);

export const resolveCadencePauseSeconds = resolveCadencePhaseSeconds;

export interface ExecutionSpeedFields {
  executionSpeed: ExerciseExecutionSpeed | null;
  isometricSeconds: number | null;
  concentricSeconds: number | null;
  pauseSeconds: number | null;
  eccentricSeconds: number | null;
}

export const emptyExecutionSpeedFields = (): ExecutionSpeedFields => ({
  executionSpeed: null,
  isometricSeconds: null,
  concentricSeconds: null,
  pauseSeconds: null,
  eccentricSeconds: null,
});

export const buildExecutionSpeedFields = (input: {
  executionSpeed?: string | null;
  isometricSeconds?: number | string | null;
  concentricSeconds?: number | string | null;
  pauseSeconds?: number | string | null;
  eccentricSeconds?: number | string | null;
} = {}): ExecutionSpeedFields => {
  const executionSpeed = normalizeExecutionSpeed(input.executionSpeed);

  if (executionSpeed === 'isometry') {
    return {
      executionSpeed,
      isometricSeconds: typeof input.isometricSeconds === 'number' && input.isometricSeconds > 0
        ? Math.trunc(input.isometricSeconds)
        : null,
      concentricSeconds: null,
      pauseSeconds: null,
      eccentricSeconds: null,
    };
  }

  if (executionSpeed === 'cadence') {
    return {
      executionSpeed,
      isometricSeconds: null,
      concentricSeconds: resolveCadencePhaseSeconds(input.concentricSeconds, DEFAULT_CONCENTRIC_SECONDS),
      pauseSeconds: resolveCadencePauseSeconds(input.pauseSeconds, DEFAULT_PAUSE_SECONDS),
      eccentricSeconds: resolveCadencePhaseSeconds(input.eccentricSeconds, DEFAULT_ECCENTRIC_SECONDS),
    };
  }

  return emptyExecutionSpeedFields();
};
